import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  UtilizationProductRow,
  UnmappedProductRow,
  UtilizationTab,
  isUnmappedTab,
  potentialWasteForRow,
} from '../../../../../utils/reports/utilizationReport';
import { Platform } from '../../../../../types/Platforms';
import { eur } from '../../../../orderhistory/utils';
import { fmtBase } from '../../../../../utils/reports/shared';
import { cn } from '../../../../../lib/utils';
import { SupplierLogo } from '../../../../ui/SupplierLogo';
import { useAppSettingsStore } from '../../../../../stores/appSettingsStore';

interface TooltipRow {
  label: string;
  value: string;
  accent?: boolean;
}
interface ChartDatum {
  label: string;
  value: number;
  logo?: { platform: string; unknownSupplierName?: string; alt: string };
  tooltip: TooltipRow[];
}

/** Geometry handed over by the table so bar N lines up with table row N. */
export interface ChartAlignment {
  /** Measured height of the table's `thead`, mirrored by the chart's title band. */
  headerHeight: number;
  /** Measured height of each visible table row, in the table's order. */
  rowHeights: number[];
}

interface OverOrderingChartProps {
  /** Already scoped by the page's tab + shared filters — the chart does no filtering. */
  rows: UtilizationProductRow[];
  /** Filtered unmapped rows, charted by spend on the `unmapped` tab. */
  unmappedRows: UnmappedProductRow[];
  /** Decides what a bar measures: |waste €|, waste € rolled up per recipe, or spend. */
  tab: UtilizationTab;
  /** When set, the chart mirrors the table exactly: `rows` are ALREADY the table's
   *  visible page in the table's sort order, so no re-sorting and no top-N cut, and
   *  every bar takes its row's measured height. Absent → standalone top-N chart. */
  align?: ChartAlignment;
}

const GREEN = '#0E8965';
const TOP_N = 12;
const TOOLTIP_WIDTH = 213;
const VIEWPORT_PAD = 8;

/**
 * Top-{TOP_N} horizontal bar chart of whatever the page tab + filters selected.
 * Tabs and filters used to live here; they now sit in `UtilizationControls` above so
 * the same selection also scopes the KPIs, the table and the donuts.
 *
 * Sits in the narrow LEFT column of the report's chart|table grid: its title band is
 * sized to line up with the table's `thead`, and the bar list scrolls inside the card
 * so both columns stay the same height.
 */
export const OverOrderingChart: React.FC<OverOrderingChartProps> = ({ rows, unmappedRows, tab, align }) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState<number | null>(null);
  const onUnmapped = isUnmappedTab(tab);
  const darkMode = useAppSettingsStore(s => s.darkMode);
  const barColor = darkMode ? '#34D399' : GREEN;

  const productTooltip = (r: UtilizationProductRow): TooltipRow[] => [
    { label: t('reports.utilization.col.ordered'), value: fmtBase(r.orderedBase, r.stdUnit) },
    { label: t('reports.utilization.col.needed'), value: fmtBase(r.neededBase, r.stdUnit) },
    { label: t('reports.utilization.col.difference'), value: `${r.wasteBase > 0 ? '+' : ''}${fmtBase(r.wasteBase, r.stdUnit)}`, accent: true },
    { label: t('reports.utilization.col.wasteEur'), value: r.wasteEur !== null ? eur(r.wasteEur) : t('reports.utilization.naValue') },
    ...(r.restaurantContributions ? [
      { label: t('reports.utilization.organization.localExcess'), value: eur(potentialWasteForRow(r)) },
      { label: t('reports.utilization.organization.localShortage'), value: eur(r.restaurantContributions.reduce((sum, local) => sum + Math.max(0, -(local.wasteEur ?? 0)), 0)) },
    ] : []),
  ];
  const productDatum = (r: UtilizationProductRow, value: number): ChartDatum => ({
    label: r.name,
    value,
    logo: { platform: r.platform, unknownSupplierName: r.unknownSupplierName, alt: r.supplier },
    tooltip: productTooltip(r),
  });

  const recipeData = useMemo<ChartDatum[]>(() => {
    const byRecipe = new Map<string, number>();
    for (const r of rows.flatMap(row => row.restaurantContributions ?? [row])) {
      if (r.wasteEur === null || r.wasteEur <= 0) continue;
      for (const name of [...r.dishes, ...r.subrecipes]) byRecipe.set(name, (byRecipe.get(name) ?? 0) + r.wasteEur);
    }
    return Array.from(byRecipe.entries()).map(([label, value]) => ({ label, value, tooltip: [{ label: t('reports.utilization.col.wasteEur'), value: eur(value), accent: true }] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, t]);

  // Rows are already tab-filtered; the tab only decides what a bar measures — the
  // Dish & sub-recipe tab rolls each product's waste up onto the recipes using it,
  // and the Not-linked tab charts spend on a different row type entirely.
  const unmappedData: ChartDatum[] = unmappedRows.map((r) => ({
    label: r.name,
    value: r.spend,
    logo: { platform: r.platform, unknownSupplierName: r.unknownSupplierName, alt: r.supplier },
    tooltip: [{ label: t('reports.utilization.col.spend'), value: eur(r.spend), accent: true }],
  }));

  const unsorted = onUnmapped
    ? unmappedData
    : tab === 'recipe'
    ? recipeData
    : rows.map((r) => productDatum(r, r.restaurantContributions
      ? tab === 'under' ? r.restaurantContributions.reduce((sum, local) => sum + Math.max(0, -(local.wasteEur ?? 0)), 0) : potentialWasteForRow(r)
      : Math.abs(r.wasteEur ?? 0)));

  // Aligned mode keeps the table's order and page as-is; standalone mode ranks + caps.
  const data = align ? unsorted : [...unsorted].sort((a, b) => b.value - a.value).slice(0, TOP_N);

  const maxValue = Math.max(1, ...data.map((d) => d.value));
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => eur(maxValue * f));

  return (
    <div className="h-full min-h-0 flex flex-col overflow-hidden bg-background dark:bg-background-dark rounded-xl shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040] p-4">
      {/* Height-matched to the table's thead band so the two columns read as one unit. */}
      <div
        className="shrink-0 flex items-center border-b border-border dark:border-border-dark"
        style={align ? { height: align.headerHeight } : undefined}
      >
        <h3 className={cn('text-xs font-medium uppercase tracking-wide text-muted dark:text-muted-dark', !align && 'py-[11px]')}>
          {t(onUnmapped ? 'reports.utilization.chart.unmappedTitle' : 'reports.utilization.chart.title')}
        </h3>
      </div>

      <div className={cn('flex-1 min-h-0 flex flex-col', align ? 'overflow-hidden' : 'overflow-y-auto custom-scrollbar py-3')}>
        {data.length === 0 ? (
          <div className="flex flex-1 items-center justify-center w-full">
            <p className="text-sm text-muted dark:text-muted-dark text-center">{t('reports.utilization.chart.empty')}</p>
          </div>
        ) : (
          <div className={align ? undefined : 'space-y-3 sm:space-y-3.5'}>
            {data.map((d, i) => {
              const width = (d.value / maxValue) * 100;
              // In aligned mode the row's measured height IS the layout — the bar centres
              // in it, so bar N sits on the same baseline as table row N.
              const rowHeight = align?.rowHeights[i];
              return (
                <div
                  key={`${d.label}-${i}`}
                  className="flex items-center gap-1.5 sm:gap-3"
                  style={rowHeight ? { height: rowHeight } : undefined}
                >
                  <div className="w-[90px] sm:w-[120px] flex-shrink-0">
                    <span
                      className="text-[11px] sm:text-xs font-medium text-textPrimary dark:text-textPrimary-dark tracking-[-0.2px] leading-4 line-clamp-2"
                      title={d.label}
                    >
                      {d.label}
                    </span>
                  </div>
                  <div
                    className="relative flex-1 min-w-0"
                    onMouseEnter={() => setHovered(i)}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <div className="relative w-full h-8 sm:h-9 rounded-full cursor-pointer">
                      <div
                        className="absolute inset-y-0 left-0 rounded-r transition-all duration-300"
                        style={{ width: `${Math.max(2, width)}%`, backgroundColor: barColor }}
                      />
                      {hovered === i && <BarTooltip datum={d} barWidthPct={width} />}
                    </div>
                  </div>
                </div>
              );
            })}
            <div className={cn('pl-[96px] sm:pl-[132px] flex justify-between text-[10px] sm:text-xs font-medium text-muted dark:text-muted-dark', align && 'pt-3')}>
              {ticks.map((label, i) => (
                <span key={i}>{label}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/** Rendered through a portal at fixed coords: the card clips its overflow, and the
 *  left edge is clamped to the viewport so long bars can't push the values off-screen. */
const BarTooltip: React.FC<{ datum: ChartDatum; barWidthPct: number }> = ({ datum, barWidthPct }) => {
  const platform = datum.logo ? Platform.fromString(datum.logo.platform) : null;
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; arrowLeft: number; placeBelow: boolean } | null>(null);

  useLayoutEffect(() => {
    const update = () => {
      const track = anchorRef.current?.parentElement;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const tipH = tipRef.current?.offsetHeight || 148;
      const pct = Math.min(100, Math.max(0, barWidthPct));
      const barEndX = rect.left + (rect.width * pct) / 100;

      let left = barEndX - TOOLTIP_WIDTH / 2;
      left = Math.max(VIEWPORT_PAD, Math.min(left, window.innerWidth - TOOLTIP_WIDTH - VIEWPORT_PAD));

      const placeBelow = rect.top - VIEWPORT_PAD < tipH + 10;
      const top = placeBelow ? rect.bottom + 10 : rect.top - tipH - 10;
      const arrowLeft = Math.max(12, Math.min(barEndX - left, TOOLTIP_WIDTH - 12));
      setPos({ top, left, arrowLeft, placeBelow });
    };

    update();
    const raf = requestAnimationFrame(update);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [barWidthPct, datum.label]);

  const arrow = 'w-2.5 h-2.5 rotate-45 bg-background dark:bg-background-dark border-border dark:border-border-dark';

  return (
    <>
      <span
        ref={anchorRef}
        className="absolute top-0 h-full w-0 pointer-events-none"
        style={{ left: `${Math.min(100, Math.max(0, barWidthPct))}%` }}
        aria-hidden
      />
      {createPortal(
        <div
          ref={tipRef}
          className={cn('fixed z-[300] pointer-events-none w-[213px]', !pos && 'invisible')}
          style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0 }}
        >
          {pos?.placeBelow && (
            <div className={cn(arrow, 'mb-[-5px] border-t border-l')} style={{ marginLeft: pos.arrowLeft - 5 }} />
          )}
          <div className="rounded-xl border border-border dark:border-border-dark bg-background dark:bg-background-dark shadow-[0px_8px_24px_rgba(16,24,40,0.16)] dark:shadow-[0px_8px_24px_rgba(0,0,0,0.4)] overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border dark:border-border-dark">
              {platform && datum.logo && (
                <SupplierLogo
                  platform={platform}
                  unknownSupplierName={datum.logo.unknownSupplierName}
                  size="small"
                  className="flex-shrink-0"
                  alt={datum.logo.alt}
                  onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                />
              )}
              <span className="text-sm font-medium text-textPrimary dark:text-textPrimary-dark truncate">{datum.label}</span>
            </div>
            <div className="px-3 py-2 flex flex-col gap-1.5">
              {datum.tooltip.map((row) => (
                <div key={row.label} className="flex items-center justify-between gap-3">
                  <span className="text-[13px] font-medium text-muted dark:text-muted-dark shrink-0">{row.label}</span>
                  <span className={cn('text-[13px] font-semibold text-right whitespace-nowrap', row.accent ? 'text-[#0E8965] dark:text-darkGreenColor-dark' : 'text-textPrimary dark:text-textPrimary-dark')}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>
          {pos && !pos.placeBelow && (
            <div className={cn(arrow, '-mt-[5px] border-b border-r')} style={{ marginLeft: pos.arrowLeft - 5 }} />
          )}
        </div>,
        document.body
      )}
    </>
  );
};

export default OverOrderingChart;
